/**
 * THE band-bar renderings of screening results (TT-40) — used identically on
 * every surface (dashboard cards, report headers; the PDF mirrors it via the
 * same defs' hex values). Never fork a per-page variant: band ranges, colors
 * AND tooltip wording live in app/utils/score-bands.ts only.
 *
 * - ScoreBandBar: 0–100 applicant score → three bands (Avg / Okay / Good
 *   Applicant); the band containing the score saturates, the rest fade;
 *   null score → all faded (pending).
 * - GuarantorBandBar: guarantor ACCEPTABILITY (boolean from the guarantor AI
 *   report) → exactly two bands (Not Acceptable / Acceptable).
 *
 * NO hover tooltip (client 2026-07-10 — the "Good/Okay/Avg Applicant" hover
 * text is gone; it superseded the 2026-07-08 whole-bar-tooltip decision).
 * The verdict remains on the aria-label ONLY — screen readers hear the
 * active band's meaning, sighted users read the saturated band. Never the
 * raw number here — tenants whose `score_display` setting enables numeric
 * scores render ScoreDisplay.tsx (which falls back to this bar) instead.
 * Callers decide WHETHER a score may be shown at all (pre-screening hides
 * it — locked decision); these components only render what they are given.
 */
import {
	GUARANTOR_BAND_DEFS,
	SCORE_BAND_DEFS,
	deriveActiveGuarantorBandIndex,
	deriveActiveScoreBandIndex,
} from '#app/utils/application-detail-v2/vendor/score-bands'
import { cn } from '#app/utils/misc'

export type BandBarSize = 'sm' | 'lg'

/** Shared pill row — the ONE rendering both bars use. */
function BandPills({
	bands,
	activeIndex,
	pendingLabel,
	size,
	fullWidth = false,
	className,
}: {
	bands: ReadonlyArray<{
		key: string
		label: string
		activeClass: string
		fadedClass: string
	}>
	activeIndex: number | null
	/** Aria verdict when no band is active yet. */
	pendingLabel: string
	size: BandBarSize
	/** Stretch the segments to fill the container WITHOUT changing height. */
	fullWidth?: boolean
	className?: string
}) {
	const verdict = activeIndex === null ? pendingLabel : bands[activeIndex].label
	// Report header (lg, not fullWidth): preferred 132px segments. Do NOT set
	// w-full here — that collapsed the bar when no guarantor chip reserved
	// width (Strong Applicant). max-w-full + shrink keeps High Risk tight too.
	const stretch = fullWidth
	const reportFixed = size === 'lg' && !fullWidth
	return (
		<div
			role="img"
			aria-label={verdict}
			className={cn(
				'flex items-center',
				size === 'lg' ? 'gap-2' : 'gap-1.5',
				stretch && 'w-full',
				reportFixed && 'max-w-full',
				className,
			)}
		>
			{bands.map((band, index) => (
				<span
					key={band.key}
					className={cn(
						'h-[30px] rounded-full transition-colors',
						reportFixed
							? 'w-[132px] min-w-[48px] shrink'
							: stretch
								? 'flex-1'
								: 'w-15',
						index === activeIndex ? band.activeClass : band.fadedClass,
					)}
				/>
			))}
		</div>
	)
}

export function ScoreBandBar({
	score,
	size = 'sm',
	fullWidth = false,
	className,
}: {
	/** Rounded 0–100 score; null = pending (all bands faded). */
	score: number | null
	size?: BandBarSize
	/** Fill the width without changing height (height is always 30px). */
	fullWidth?: boolean
	className?: string
}) {
	return (
		<BandPills
			bands={SCORE_BAND_DEFS}
			activeIndex={deriveActiveScoreBandIndex(score)}
			pendingLabel="Score pending"
			size={size}
			fullWidth={fullWidth}
			className={className}
		/>
	)
}

/**
 * Continuous score gauge (report header numeric mode, client mock 2026-07-22):
 * a single bar filled to the score %, tinted by the ACTIVE band's hue — the
 * NN/100 above carries the value, this shows where it sits at a glance. Fill
 * colour derives from SCORE_BAND_DEFS (the same source as the band bar), so a
 * 49 reads red (below the 50 cutoff) — red 0–49, yellow 50–79, green 80–100.
 * Pending (no score) → empty track. Used by the numeric-mode report header AND
 * the dashboard cards + pipeline (client 2026-07-24 — the dashboard score shows
 * the same bar as everywhere else); band mode and the listing rows' pill keep
 * the band bar (no gauge). Its PDF twin lives in pdf/primitives.tsx.
 */
export function ScoreGaugeBar({
	score,
	size = 'lg',
	className,
}: {
	/** Rounded 0–100 score; null = pending (empty track). */
	score: number | null
	/** Height is always 30px on every surface. */
	size?: BandBarSize
	className?: string
}) {
	const index = deriveActiveScoreBandIndex(score)
	const band = index === null ? null : SCORE_BAND_DEFS[index]
	const pct = score == null ? 0 : Math.min(100, Math.max(0, Math.round(score)))
	return (
		<div
			role="img"
			aria-label={band ? band.label : 'Score pending'}
			className={cn(
				'h-[30px] w-full overflow-hidden rounded-full bg-[#F0F0F0]',
				className,
			)}
		>
			{band ? (
				<div
					className="h-full rounded-full transition-all"
					style={{ width: `${pct}%`, backgroundColor: band.hex }}
				/>
			) : null}
		</div>
	)
}

export function GuarantorBandBar({
	acceptable,
	size = 'sm',
	className,
}: {
	/** Guarantor AI verdict; null = pending (both bands faded). */
	acceptable: boolean | null
	size?: BandBarSize
	className?: string
}) {
	return (
		<BandPills
			bands={GUARANTOR_BAND_DEFS}
			activeIndex={deriveActiveGuarantorBandIndex(acceptable)}
			pendingLabel="Guarantor verdict pending"
			size={size}
			className={className}
		/>
	)
}
